import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { formatApiErrorDetail } from "../lib/api";
import useBrandIdentity from "../hooks/useBrandIdentity";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { logo } = useBrandIdentity();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Bem-vindo, ${u.name}!`);
      const params = new URLSearchParams(loc.search);
      const redirect = params.get("redirect");
      const dest = redirect || loc.state?.from || (u.role === "MERCHANT" ? "/merchant" : u.role === "ADMIN" ? "/admin" : "/wallet");
      navigate(dest);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      const status = e.response?.status;
      // E-mail não verificado → redireciona para tela de confirmação
      if (status === 403 && /confirme.*e-?mail|verif/i.test(detail || "")) {
        toast.info("Confirme seu e-mail para acessar. Te levei para a tela de confirmação.");
        const looksLikeEmail = email.includes("@");
        navigate(`/verify-email${looksLikeEmail ? `?email=${encodeURIComponent(email)}` : ""}`);
        return;
      }
      setError(detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <Card className="w-full max-w-md p-8 rounded-2xl shadow-xl border-stone-200">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Morar Bem" className="h-16 w-16 object-contain mb-2"/>
        </div>
        <h1 className="font-display text-3xl font-bold text-stone-900 text-center">Entrar</h1>
        <p className="text-sm text-stone-500 mt-1 text-center">Acesse sua conta Morar Bem</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">E-mail ou telefone</Label>
            <Input id="email" type="text" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com ou (99) 99999-9999" className="rounded-xl mt-1" data-testid="login-email"/>
          </div>
          <div>
            <Label htmlFor="password">Senha</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl mt-1" data-testid="login-password"/>
            <div className="text-right mt-1"><Link to="/forgot-password" className="text-xs text-blue-700 hover:underline" data-testid="goto-forgot">Esqueci minha senha</Link></div>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl h-11" data-testid="login-submit">
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </form>
        <p className="text-sm text-stone-500 mt-4 text-center">
          Não tem conta? <Link to="/register" className="text-blue-900 font-semibold hover:underline" data-testid="goto-register">Criar conta</Link>
        </p>
      </Card>
    </div>
  );
}
